import type { ExerciseTemplate } from '@yuan-math/exercises';
import { roundTo } from '@yuan-math/math';
import { choiceExercise, expr, leadingTerm, numericExercise, options, signedConstant, template, trap } from './authoring.ts';

/**
 * Multi-step equations and inequalities — the year an equation becomes a
 * sentence — and then the Grade 8 question that sentence raises: how many
 * numbers satisfy it? The templates at the end deliberately do not always have
 * a single answer waiting at the end of the algebra.
 */
export const EQUATION_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'equation-two-step',
    conceptId: 'multi-step-equations',
    description: 'Solve ax + b = c, undoing the operations in reverse order.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, difficulty === 2 ? 6 : 12);
      const x = difficulty === 2 ? rng.int(2, 9) : rng.pick([-9, -7, -6, -5, -4, -3, -2, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12]);
      const b = rng.int(2, difficulty === 2 ? 12 : 24) * (difficulty === 2 ? 1 : rng.bool() ? 1 : -1);
      const c = a * x + b;

      return numericExercise({
        prompt: 'Solve for x.',
        latex: `${a}x ${signedConstant(b)} = ${c}`,
        value: x,
        summary: `Take ${b < 0 ? `${-b} off both sides by adding` : `${b} off both sides`}, leaving ${a}x = ${a * x}, then divide by ${a}.`,
        hint: 'x was multiplied, then something was added. Undo them the other way round.',
        steps: [
          { label: `${b < 0 ? 'Add' : 'Subtract'} ${Math.abs(b)} on both sides.`, latex: `${a}x = ${a * x}` },
          { label: `Divide both sides by ${a}.`, latex: `x = ${x}` },
          { label: 'Check by substituting back.', latex: `${a}(${x}) ${signedConstant(b)} = ${c}` },
        ],
        // Dividing before subtracting — unwinding the expression inside out.
        traps: [trap(roundTo(c / a - b, 4), 'undo-out-of-order')],
      });
    },
  }),

  template({
    id: 'equation-distribute',
    conceptId: 'multi-step-equations',
    description: 'Solve a(x + b) = c, where the bracket must be expanded in full.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, difficulty === 4 ? 9 : 6);
      const b = rng.pick([-8, -6, -5, -4, -3, -2, 2, 3, 4, 5, 6, 8]);
      const x = rng.int(2, difficulty === 4 ? 15 : 10);
      const c = a * (x + b);

      return numericExercise({
        prompt: 'Solve for x.',
        latex: `${a}\\left(x ${signedConstant(b)}\\right) = ${c}`,
        value: x,
        summary: `Divide both sides by ${a} to get x ${signedConstant(b)} = ${x + b}, then ${b < 0 ? 'add' : 'subtract'} ${Math.abs(b)}.`,
        hint: `Either divide both sides by ${a} first, or expand the bracket — but if you expand, the ${a} multiplies both terms inside.`,
        steps: [
          { label: 'Expand the bracket in full.', latex: `${a}x ${signedConstant(a * b)} = ${c}` },
          { label: `${a * b < 0 ? 'Add' : 'Subtract'} ${Math.abs(a * b)} on both sides.`, latex: `${a}x = ${a * x}` },
          { label: `Divide by ${a}.`, latex: `x = ${x}` },
        ],
        // Expanding as ax + b, with the bracket's second term left unmultiplied.
        traps: [trap(roundTo((c - b) / a, 4), 'distribute-first-term-only')],
      });
    },
  }),

  template({
    id: 'equation-both-sides',
    conceptId: 'multi-step-equations',
    description: 'Solve an equation with the unknown on both sides.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const x = rng.int(2, difficulty === 5 ? 15 : 10);
      const a = rng.int(3, 9);
      const smaller = rng.int(1, a - 1);
      const b = rng.int(1, 20);
      const d = (a - smaller) * x + b;

      return numericExercise({
        prompt: 'Solve for x.',
        latex: `${leadingTerm(a)} + ${b} = ${leadingTerm(smaller)} + ${d}`,
        value: x,
        summary: `Take ${smaller}x from both sides to get ${a - smaller}x + ${b} = ${d}, then solve as usual.`,
        hint: 'Collect all the x terms on one side before you touch the numbers.',
        steps: [
          { label: `Subtract ${smaller}x from both sides.`, latex: `${a - smaller}x + ${b} = ${d}` },
          { label: `Subtract ${b}.`, latex: `${a - smaller}x = ${d - b}` },
          { label: `Divide by ${a - smaller}.`, latex: `x = ${x}` },
        ],
        traps: [
          // Solving ax + b = d, with the smaller x term simply ignored.
          trap(roundTo((d - b) / a, 4), 'variable-term-left-behind'),
          // Adding the x terms rather than subtracting them.
          trap(roundTo((d - b) / (a + smaller), 4), 'variable-term-left-behind'),
        ],
      });
    },
  }),

  template({
    id: 'inequality-solve-direction',
    conceptId: 'inequalities',
    description: 'Solve a one-step inequality and choose the right direction.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const magnitude = rng.int(2, 9);
      const negativeCoefficient = rng.bool();
      const a = negativeCoefficient ? -magnitude : magnitude;
      const k = rng.pick([-8, -6, -5, -4, -3, -2, 2, 3, 4, 5, 6, 8]);
      const c = a * k;
      const strictlyLess = rng.bool();

      // Dividing by a negative reverses the direction. That is the only rule
      // here that does not carry over from equations, so it is the whole item.
      const kept = strictlyLess ? '<' : '>';
      const flipped = strictlyLess ? '>' : '<';
      const correctSign = negativeCoefficient ? flipped : kept;
      const wrongSign = negativeCoefficient ? kept : flipped;

      const { choices, correctId } = options(
        [
          { latex: `x ${correctSign} ${k}`, correct: true },
          { latex: `x ${wrongSign} ${k}`, misconceptionId: 'inequality-sign-not-flipped' },
          { latex: `x ${correctSign} ${-k}`, misconceptionId: 'negative-product-sign' },
          { latex: `x ${wrongSign} ${-k}`, misconceptionId: 'inequality-sign-not-flipped' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Solve this inequality.',
        latex: `${a}x ${kept} ${c}`,
        choices,
        correctId,
        summary: negativeCoefficient
          ? `Dividing both sides by ${a} reverses the inequality, giving x ${correctSign} ${k}.`
          : `Dividing both sides by ${a} leaves the direction alone, giving x ${correctSign} ${k}.`,
        hint: `You are dividing by ${a}. Does that turn the number line around?`,
        steps: [
          { label: `Divide both sides by ${a}.`, latex: `x ${correctSign} ${k}` },
          {
            label: negativeCoefficient
              ? 'Dividing by a negative reverses the direction.'
              : 'The divisor is positive, so the direction is unchanged.',
            latex: `x ${correctSign} ${k}`,
          },
        ],
      });
    },
  }),

  template({
    id: 'inequality-boundary',
    conceptId: 'inequalities',
    description: 'Solve a two-step inequality and give the boundary value.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, difficulty === 4 ? 9 : 5);
      const k = rng.pick([-9, -7, -5, -4, -3, -2, 2, 3, 4, 5, 7, 9]);
      const b = rng.int(2, 20) * (rng.bool() ? 1 : -1);
      const c = a * k + b;
      const strictlyLess = rng.bool();
      const sign = strictlyLess ? '\\le' : '\\ge';
      const words = strictlyLess ? 'at most' : 'at least';

      return numericExercise({
        prompt: `Solve this inequality. What is the boundary value — the number that x must be ${words}?`,
        latex: `${a}x ${signedConstant(b)} ${sign} ${c}`,
        value: k,
        summary: `Solving gives x ${strictlyLess ? '≤' : '≥'} ${k}, so the boundary is ${k}.`,
        hint: 'Solve it exactly as you would an equation. The coefficient is positive, so the direction never changes.',
        steps: [
          { label: `${b < 0 ? 'Add' : 'Subtract'} ${Math.abs(b)} on both sides.`, latex: `${a}x ${sign} ${a * k}` },
          { label: `Divide by ${a}, which is positive, so the sign stays.`, latex: `x ${sign} ${k}` },
        ],
        traps: [trap(roundTo(c / a - b, 4), 'undo-out-of-order')],
      });
    },
  }),

  /* ------------------------------------------- how many solutions are there */
  template({
    id: 'equation-variable-both-sides',
    conceptId: 'linear-equation-solutions',
    description: 'Solve with the unknown on both sides, collecting the variable terms first.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(3, difficulty === 4 ? 12 : 8);
      const c = rng.int(1, a - 1);
      const x = rng.pick([-8, -6, -5, -4, -3, -2, 2, 3, 4, 5, 6, 7, 8, 9]);
      const b = rng.int(-15, 15);
      const d = (a - c) * x + b;

      return numericExercise({
        prompt: 'Solve for x.',
        latex: expr(leadingTerm(a), signedConstant(b), '=', leadingTerm(c), signedConstant(d)),
        value: x,
        summary: `Subtracting ${leadingTerm(c)} from both sides leaves ${expr(leadingTerm(a - c), signedConstant(b))} = ${d}, and solving that gives x = ${x}.`,
        hint: 'There is an x on both sides. Get them both onto one side before you touch the numbers.',
        steps: [
          { label: `Subtract ${leadingTerm(c)} from both sides.`, latex: expr(leadingTerm(a - c), signedConstant(b), '=', String(d)) },
          { label: `${b < 0 ? 'Add' : 'Subtract'} ${Math.abs(b)}.`, latex: `${leadingTerm(a - c)} = ${(a - c) * x}` },
          { label: `Divide by ${a - c}.`, latex: `x = ${x}` },
        ],
        // Adding the other side's x term instead of subtracting it — the sign
        // carried across the equals but not applied.
        traps: [
          ...(((a - c) * x) / (a + c) === x
            ? []
            : [trap(roundTo(((a - c) * x) / (a + c), 4), 'variable-term-left-behind')]),
        ],
      });
    },
  }),

  template({
    id: 'equation-solution-count',
    conceptId: 'linear-equation-solutions',
    description: 'Say how many solutions an equation has, including none and all of them.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const kind = rng.pick(['one', 'none', 'many'] as const);
      const a = rng.int(2, 9);
      const b = rng.int(2, 15);
      let solution = rng.int(2, 9);
      let latex: string;

      if (kind === 'one') {
        const c = rng.int(1, Math.max(1, a - 1));
        const x = rng.int(2, 9);
        latex = expr(leadingTerm(a), signedConstant(b), '=', leadingTerm(c), signedConstant((a - c) * x + b));
        solution = x;
      } else if (kind === 'none') {
        latex = expr(leadingTerm(a), signedConstant(b), '=', leadingTerm(a), signedConstant(b + rng.int(1, 9)));
      } else {
        const k = rng.int(2, 5);
        latex = `${k}\\left(${expr(leadingTerm(a), signedConstant(b))}\\right) = ${expr(leadingTerm(k * a), signedConstant(k * b))}`;
      }

      const { choices, correctId } = options(
        [
          {
            label: `Exactly one solution, x = ${solution}`,
            correct: kind === 'one',
            ...(kind === 'one' ? {} : { misconceptionId: 'no-solution-called-zero' }),
          },
          {
            label: 'No solution — no number makes it true',
            correct: kind === 'none',
            ...(kind === 'none'
              ? {}
              : { misconceptionId: kind === 'many' ? 'always-true-never-true-confused' : 'variable-term-left-behind' }),
          },
          {
            label: 'Infinitely many solutions — every number makes it true',
            correct: kind === 'many',
            ...(kind === 'many'
              ? {}
              : { misconceptionId: kind === 'none' ? 'always-true-never-true-confused' : 'variable-term-left-behind' }),
          },
          { label: 'Exactly one solution, x = 0', misconceptionId: 'no-solution-called-zero' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'How many solutions does this equation have?',
        latex,
        choices,
        correctId,
        summary:
          kind === 'one'
            ? 'The two sides have different numbers of xs, so they describe lines that cross exactly once — one solution.'
            : kind === 'none'
              ? 'Both sides climb at the same rate from different starting points, so the xs cancel and leave a false statement. Nothing satisfies it.'
              : 'Expanding the bracket gives exactly the right-hand side, so the equation is true whatever x is.',
        hint: 'Expand any brackets, then compare how many xs are on each side before you solve anything.',
      });
    },
  }),

  template({
    id: 'equation-choose-coefficient',
    conceptId: 'linear-equation-solutions',
    description: 'Choose a coefficient that forces a particular number of solutions.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const a = rng.int(2, 9);
      const b = rng.int(2, 15);
      const wantNone = rng.bool();
      const d = wantNone ? b + rng.int(1, 9) : b;

      return numericExercise({
        prompt: `For what value of k does this equation have ${wantNone ? 'no solution' : 'infinitely many solutions'}?`,
        latex: `${leadingTerm(a)} + ${b} = kx + ${d}`,
        value: a,
        summary: wantNone
          ? `With k = ${a} both sides climb at the same rate but the constants differ (${b} against ${d}), so the xs cancel and leave a false statement.`
          : `With k = ${a} the two sides become identical, so every number satisfies the equation.`,
        hint: 'The xs have to cancel, and that only happens when the two coefficients match. Then look at what the constants leave behind.',
        steps: [
          { label: 'Subtract kx from both sides.', latex: `(${a} - k)x + ${b} = ${d}` },
          { label: 'For the x term to vanish, its coefficient must be zero.', latex: `${a} - k = 0` },
          {
            label: wantNone
              ? `And ${b} is not ${d}, so nothing is left that can be true.`
              : 'And the constants already agree, so what is left is true for every x.',
            latex: `k = ${a}`,
          },
        ],
        traps: [trap(0, 'no-solution-called-zero')],
      });
    },
  }),
];
